import React, {useContext, useState, useEffect} from 'react';
import {Box, Button, Container, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {ExitToApp, Group, Settings, SignalCellular3Bar} from '@mui/icons-material';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import i18next from 'i18next';
import {AuthContext} from '../App';
import {isAdmin} from '../utils/Authentication';
import 'flag-icons/css/flag-icons.min.css';

const FullscreenMenu = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [headerVisible, setHeaderVisible] = useState(true);
	const [mousePos, setMousePos] = useState({x: 0.5, y: 0.5});
	const [logoPulse, setLogoPulse] = useState(false);
	const {t} = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const {state, dispatch} = useContext(AuthContext);

	// Hamburger attention pulse after 4 seconds (3 pulses)
	useEffect(() => {
		const timer = setTimeout(() => {
			let count = 0;
			const pulse = () => {
				setLogoPulse(true);
				setTimeout(() => setLogoPulse(false), 600);
				count++;
				if (count < 3) setTimeout(pulse, 1500);
			};
			pulse();
		}, 4000);
		return () => clearTimeout(timer);
	}, []);

	// Smart header — show on scroll up, hide on scroll down
	useEffect(() => {
		let lastScrollY = window.scrollY;
		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			if (currentScrollY < 80) {
				setHeaderVisible(true);
			} else if (currentScrollY < lastScrollY - 5) {
				setHeaderVisible(true);
			} else if (currentScrollY > lastScrollY + 5) {
				setHeaderVisible(false);
			}
			lastScrollY = currentScrollY;
		};
		window.addEventListener('scroll', handleScroll, {passive: true});
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	// Lock body scroll when menu is open + track mouse for parallax
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
			const handleMouseMove = (e) => {
				setMousePos({
					x: (e.clientX / window.innerWidth - 0.5) * 2,
					y: (e.clientY / window.innerHeight - 0.5) * 2,
				});
			};
			window.addEventListener('mousemove', handleMouseMove);
			return () => {
				document.body.style.overflow = '';
				window.removeEventListener('mousemove', handleMouseMove);
			};
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	const handleChangeLanguage = newLanguage => {
		localStorage.setItem('redmanagement-language', newLanguage);
		i18next.changeLanguage(newLanguage.split('-')[0], (err, t) => {
			if (err) return console.log('language changing failed.', err);
			window.location.reload();
		});
	};

	const handleLogout = e => {
		e.preventDefault();
		try {
			dispatch({type: 'LOGOUT'});
			setIsOpen(false);
			history.push('/');
		} catch (error) {
			console.error('Logout error:', error);
			window.location.href = '/';
		}
	};

	const navigate = path => {
		setIsOpen(false);
		setTimeout(() => history.push(path), 150);
	};

	const mainLinks = [
		{path: '/tjänster', label: t('topbar.services')},
		{path: '/projekt', label: t('topbar.projects')},
		{path: '/om-oss', label: t('topbar.about')},
	];

	const secondaryLinks = [
		{path: '/kontakt', label: t('topbar.contact')},
		{path: '/karriär', label: t('topbar.career')},
		{path: '/nyheter', label: t('topbar.news')},
	];

	const serviceLinks = [
		{label: t('startpage.analysis'), index: 0},
		{label: t('startpage.duediligence'), index: 1},
		{label: t('startpage.realestatedevelopment'), index: 2},
		{label: t('startpage.sustainability'), index: 3},
		{label: t('startpage.builddevrep'), index: 4},
		{label: t('startpage.projectmanagement'), index: 5},
	];

	return (
		<>
			{/* Admin Bar — kept exactly as before */}
			{state.isAuthenticated && (
				<Box
					sx={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						zIndex: 1400,
						background: '#4cae52',
						color: 'white',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						padding: {xs: '8px 1rem', sm: '8px', md: '8px 1rem'},
					}}
				>
					<Container maxWidth="xl">
						<Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
							<Box sx={{display: 'flex', alignItems: 'center', gap: {xs: 0, sm: 1, md: 2}}}>
								{isAdmin && (
									<Button
										size="small"
										startIcon={<SignalCellular3Bar />}
										onClick={() =>
											window.open('https://analytics.google.com/analytics/p339433877', '_blank')
										}
										sx={{
											color: '#FFFFFF',
											textTransform: 'none',
											px: 2,
											fontSize: {xs: '0.7rem', md: '0.875rem'},
											'&:hover': {bgcolor: alpha('#FFFFFF', 0.1)},
										}}
									>
										Google Analytics
									</Button>
								)}
							</Box>
							<Box sx={{display: 'flex', gap: {sm: 0, md: 2}}}>
								<Button
									startIcon={<Group />}
									onClick={() => history.push('/internwebb')}
									sx={{
										color: '#FFFFFF',
										textTransform: 'none',
										fontSize: {xs: '0.7rem', md: '0.875rem'},
										'&:hover': {bgcolor: alpha('#FFFFFF', 0.1)},
									}}
								>
									Internwebb
								</Button>
								<Button
									startIcon={<Settings />}
									onClick={() =>
										history.push('/kontakt/' + JSON.parse(localStorage.getItem('email')))
									}
									sx={{
										color: '#FFFFFF',
										textTransform: 'none',
										fontSize: {xs: '0.7rem', md: '0.875rem'},
										display: {xs: 'none', sm: 'inline-flex'},
										'&:hover': {bgcolor: alpha('#FFFFFF', 0.1)},
									}}
								>
									{JSON.parse(localStorage.getItem('firstName')) +
										' ' +
										JSON.parse(localStorage.getItem('lastName'))}
								</Button>
								<Button
									startIcon={<ExitToApp />}
									onClick={handleLogout}
									sx={{
										color: '#FFFFFF',
										textTransform: 'none',
										fontSize: {xs: '0.7rem', md: '0.875rem'},
										'&:hover': {bgcolor: alpha('#FFFFFF', 0.1)},
									}}
								>
									Logga ut
								</Button>
							</Box>
						</Box>
					</Container>
				</Box>
			)}

			{/* Header bar — logo + nav + language (smart show/hide) */}
			<Box
				sx={{
					position: 'fixed',
					top: state.isAuthenticated ? '44px' : 0,
					left: 0,
					right: 0,
					zIndex: 1300,
					display: 'flex',
					flexDirection: 'column',
					transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
					bgcolor: isOpen ? 'transparent' : '#ffffff',
					borderBottom: isOpen ? 'none' : '1px solid rgba(0,0,0,0.06)',
					transform: (!headerVisible && !isOpen) || isOpen ? 'translateY(-100%)' : 'translateY(0)',
					opacity: isOpen ? 0 : 1,
					pointerEvents: isOpen ? 'none' : 'auto',
					boxShadow: headerVisible && window.scrollY > 80 ? '0 2px 12px rgba(0,0,0,0.08)' : 'none',
				}}
			>
				{/* Green accent line — animated gradient */}
				<Box
					sx={{
						height: '4px',
						minHeight: '4px',
						width: '100%',
						background: 'linear-gradient(90deg, #2e7d32 0%, #4cae52 30%, #66bb6a 50%, #4cae52 70%, #2e7d32 100%)',
						backgroundSize: '200% 100%',
						animation: 'shimmer 8s ease-in-out infinite',
						'@keyframes shimmer': {
							'0%': {backgroundPosition: '200% 0'},
							'50%': {backgroundPosition: '0% 0'},
							'100%': {backgroundPosition: '200% 0'},
						},
					}}
				/>
				{/* Header content */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						px: {xs: 2, md: 5},
						py: {xs: 1.2, md: 1.5},
					}}
				>
				{/* Logo */}
				<Link to="/" onClick={() => setIsOpen(false)} style={{textDecoration: 'none', zIndex: 1301}}>
					<Box
						sx={{
							transition: 'all 0.3s ease',
							'&:hover': {transform: 'scale(1.02)'},
							'& img': {
								width: {xs: '90px', sm: '110px', md: '130px'},
								height: 'auto',
								display: 'block',
							},
						}}
					>
						<img
							src="https://storage.googleapis.com/redmanagement/redm_logo_xl_horizontal.png"
							alt="RED Management"
						/>
					</Box>
				</Link>

				{/* Center — Navigation links (desktop only) */}
				<Box
					sx={{
						display: {xs: 'none', md: 'flex'},
						gap: 1,
						alignItems: 'center',
					}}
				>
					{mainLinks.map(link => {
						const isActive = location.pathname.startsWith(link.path);
						return (
							<Box
								key={link.path}
								onClick={() => { setIsOpen(false); history.push(link.path); }}
								sx={{
									position: 'relative',
									cursor: 'pointer',
									px: 1.5,
									py: 0.5,
									'&:hover .nav-underline': {
										width: '100%',
									},
									'&:hover .nav-label': {
										color: '#4cae52',
									},
								}}
							>
								<Typography
									className="nav-label"
									sx={{
										color: isActive ? '#4cae52' : '#2a2a2a',
										fontFamily: 'Titillium Web',
										fontSize: '0.88rem',
										fontWeight: 600,
										letterSpacing: '0.04em',
										textTransform: 'uppercase',
										transition: 'color 0.3s ease',
									}}
								>
									{link.label}
								</Typography>
								<Box
									className="nav-underline"
									sx={{
										position: 'absolute',
										bottom: 0,
										left: '50%',
										transform: 'translateX(-50%)',
										width: isActive ? '60%' : '0%',
										height: '2px',
										bgcolor: '#4cae52',
										borderRadius: '1px',
										transition: 'width 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
									}}
								/>
							</Box>
						);
					})}
					<Box sx={{width: '1px', height: 20, bgcolor: 'rgba(0,0,0,0.1)', mx: 1}} />
					{secondaryLinks.map(link => {
						const isActive = location.pathname.startsWith(link.path);
						return (
							<Box
								key={link.path}
								onClick={() => { setIsOpen(false); history.push(link.path); }}
								sx={{
									position: 'relative',
									cursor: 'pointer',
									px: 1,
									py: 0.5,
									'&:hover .nav-underline': {
										width: '100%',
									},
									'&:hover .nav-label': {
										color: '#4cae52',
									},
								}}
							>
								<Typography
									className="nav-label"
									sx={{
										color: isActive ? '#4cae52' : '#888',
										fontFamily: 'Titillium Web',
										fontSize: '0.88rem',
										fontWeight: 500,
										transition: 'color 0.3s ease',
									}}
								>
									{link.label}
								</Typography>
								<Box
									className="nav-underline"
									sx={{
										position: 'absolute',
										bottom: 0,
										left: '50%',
										transform: 'translateX(-50%)',
										width: isActive ? '60%' : '0%',
										height: '2px',
										bgcolor: '#4cae52',
										borderRadius: '1px',
										transition: 'width 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
									}}
								/>
							</Box>
						);
					})}
				</Box>

				{/* Right — Language flags + spacer for hamburger */}
				<Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, background: 'rgba(76,174,82,0.08)', borderRadius: '30px', px: 1.5, py: 0.5}}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 0.5,
							mr: {xs: 0.5, md: 2},
						}}
					>
						<Box
							onClick={() => handleChangeLanguage('sv-SE')}
							sx={{
								cursor: 'pointer',
								width: 36,
								height: 36,
								borderRadius: '50%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								transition: 'all 0.2s ease',
								'&:hover': {
									transform: 'scale(1.1)',
								},
							}}
						>
							<span className="fi fi-se" style={{width: i18next.language === 'sv' ? 28 : 21, height: i18next.language === 'sv' ? 21 : 16, borderRadius: 2, display: 'inline-block', backgroundSize: 'cover', transition: 'all 0.2s ease', boxShadow: i18next.language === 'sv' ? '0 1px 4px rgba(0,0,0,0.2)' : 'none'}} />
						</Box>
						<Box
							onClick={() => handleChangeLanguage('en-US')}
							sx={{
								cursor: 'pointer',
								width: 36,
								height: 36,
								borderRadius: '50%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								transition: 'all 0.2s ease',
								'&:hover': {
									transform: 'scale(1.1)',
								},
							}}
						>
							<span className="fi fi-gb" style={{width: i18next.language === 'en' ? 28 : 21, height: i18next.language === 'en' ? 21 : 16, borderRadius: 2, display: 'inline-block', backgroundSize: 'cover', transition: 'all 0.2s ease', boxShadow: i18next.language === 'en' ? '0 1px 4px rgba(0,0,0,0.2)' : 'none'}} />
						</Box>
					</Box>
					{/* Spacer for hamburger */}
					<Box sx={{width: {xs: 48, md: 56}, height: {xs: 48, md: 56}}} />
				</Box>
			</Box>
			</Box>

			{/* Floating hamburger — always visible, 3 lines morph to X */}
			<Box
				onClick={() => setIsOpen(!isOpen)}
				sx={{
					position: 'fixed',
					top: state.isAuthenticated
						? {xs: 'calc(44px + 12px)', md: 'calc(44px + 16px)'}
						: {xs: '12px', md: '16px'},
					right: {xs: '16px', md: '32px'},
					zIndex: 1301,
					width: {xs: 48, md: 56},
					height: {xs: 48, md: 56},
					borderRadius: '50%',
					bgcolor: isOpen ? 'white' : '#4cae52',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					cursor: 'pointer',
					transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
					gap: isOpen ? 0 : '5px',
					boxShadow: logoPulse
						? '0 0 0 6px rgba(76,174,82,0.25), 0 0 20px rgba(76,174,82,0.3)'
						: !headerVisible && !isOpen ? '0 2px 12px rgba(0,0,0,0.15)' : 'none',
					transform: logoPulse ? 'scale(1.15)' : 'scale(1)',
					'&:hover': {
						transform: 'scale(1.08)',
					},
				}}
			>
				{/* Top line */}
				<Box
					sx={{
						width: isOpen ? 20 : 20,
						height: 2,
						bgcolor: isOpen ? '#1a1a1a' : 'white',
						borderRadius: 1,
						transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
						transform: isOpen ? 'rotate(45deg) translate(3.5px, 3.5px)' : 'none',
					}}
				/>
				{/* Middle line */}
				<Box
					sx={{
						width: isOpen ? 0 : 14,
						height: 2,
						bgcolor: isOpen ? '#1a1a1a' : 'white',
						borderRadius: 1,
						transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
						opacity: isOpen ? 0 : 1,
						alignSelf: 'flex-end',
						mr: isOpen ? 0 : {xs: '13px', md: '17px'},
					}}
				/>
				{/* Bottom line */}
				<Box
					sx={{
						width: isOpen ? 20 : 20,
						height: 2,
						bgcolor: isOpen ? '#1a1a1a' : 'white',
						borderRadius: 1,
						transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
						transform: isOpen ? 'rotate(-45deg) translate(3.5px, -3.5px)' : 'none',
					}}
				/>
			</Box>

			{/* Fullscreen overlay — circle reveal from hamburger */}
			<Box
				sx={{
					position: 'fixed',
					inset: 0,
					zIndex: 1299,
					bgcolor: '#388e3c',
					clipPath: isOpen
						? 'circle(150% at calc(100% - 60px) 44px)'
						: 'circle(0% at calc(100% - 60px) 44px)',
					transition: 'clip-path 0.8s cubic-bezier(0.65, 0, 0.35, 1)',
					display: 'flex',
					flexDirection: 'column',
					overflowX: 'hidden',
					overflowY: {xs: 'auto', md: 'hidden'},
				}}
			>
				{/* Decorative background circles — parallax */}
				<Box
					sx={{
						position: 'absolute',
						width: '600px',
						height: '600px',
						borderRadius: '50%',
						border: '1px solid rgba(255,255,255,0.06)',
						top: '10%',
						left: '-10%',
						transform: `translate(${mousePos.x * -15}px, ${mousePos.y * -15}px)`,
						transition: 'transform 0.3s ease-out',
						pointerEvents: 'none',
					}}
				/>
				<Box
					sx={{
						position: 'absolute',
						width: '400px',
						height: '400px',
						borderRadius: '50%',
						border: '1px solid rgba(255,255,255,0.04)',
						bottom: '5%',
						right: '-5%',
						transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)`,
						transition: 'transform 0.3s ease-out',
						pointerEvents: 'none',
					}}
				/>

				{/* Architectural blueprint element — right side */}
				<Box
					component="svg"
					viewBox="0 0 300 500"
					sx={{
						position: 'absolute',
						right: {xs: '-5%', md: '5%'},
						top: '50%',
						transform: `translateY(-50%) translate(${mousePos.x * 10}px, ${mousePos.y * -8}px)`,
						transition: 'transform 0.4s ease-out',
						width: {xs: '200px', md: '280px'},
						height: 'auto',
						pointerEvents: 'none',
						opacity: isOpen ? 0.1 : 0,
						transitionProperty: 'transform, opacity',
						transitionDuration: '0.4s, 0.8s',
						transitionDelay: '0s, 0.3s',
					}}
				>
					{/* Building 1 — tall tower */}
					<rect x="30" y="80" width="60" height="420" fill="none" stroke="white" strokeWidth="0.8" />
					<line x1="30" y1="120" x2="90" y2="120" stroke="white" strokeWidth="0.5" />
					<line x1="30" y1="160" x2="90" y2="160" stroke="white" strokeWidth="0.5" />
					<line x1="30" y1="200" x2="90" y2="200" stroke="white" strokeWidth="0.5" />
					<line x1="30" y1="240" x2="90" y2="240" stroke="white" strokeWidth="0.5" />
					<line x1="30" y1="280" x2="90" y2="280" stroke="white" strokeWidth="0.5" />
					<line x1="30" y1="320" x2="90" y2="320" stroke="white" strokeWidth="0.5" />
					<line x1="30" y1="360" x2="90" y2="360" stroke="white" strokeWidth="0.5" />
					<line x1="30" y1="400" x2="90" y2="400" stroke="white" strokeWidth="0.5" />
					<line x1="30" y1="440" x2="90" y2="440" stroke="white" strokeWidth="0.5" />
					<line x1="60" y1="80" x2="60" y2="500" stroke="white" strokeWidth="0.4" />
					{/* Windows */}
					<rect x="38" y="90" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="68" y="90" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="38" y="130" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="68" y="130" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="38" y="170" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="68" y="170" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="38" y="210" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="68" y="210" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="38" y="250" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="68" y="250" width="14" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					{/* Antenna */}
					<line x1="60" y1="80" x2="60" y2="50" stroke="white" strokeWidth="0.6" />
					<line x1="52" y1="60" x2="68" y2="60" stroke="white" strokeWidth="0.4" />

					{/* Building 2 — medium block */}
					<rect x="110" y="180" width="80" height="320" fill="none" stroke="white" strokeWidth="0.8" />
					<line x1="110" y1="220" x2="190" y2="220" stroke="white" strokeWidth="0.5" />
					<line x1="110" y1="260" x2="190" y2="260" stroke="white" strokeWidth="0.5" />
					<line x1="110" y1="300" x2="190" y2="300" stroke="white" strokeWidth="0.5" />
					<line x1="110" y1="340" x2="190" y2="340" stroke="white" strokeWidth="0.5" />
					<line x1="110" y1="380" x2="190" y2="380" stroke="white" strokeWidth="0.5" />
					<line x1="110" y1="420" x2="190" y2="420" stroke="white" strokeWidth="0.5" />
					<line x1="110" y1="460" x2="190" y2="460" stroke="white" strokeWidth="0.5" />
					<line x1="150" y1="180" x2="150" y2="500" stroke="white" strokeWidth="0.4" />
					{/* Windows */}
					<rect x="118" y="190" width="24" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="158" y="190" width="24" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="118" y="230" width="24" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="158" y="230" width="24" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="118" y="270" width="24" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="158" y="270" width="24" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					{/* Roof detail */}
					<line x1="110" y1="180" x2="150" y2="160" stroke="white" strokeWidth="0.5" />
					<line x1="190" y1="180" x2="150" y2="160" stroke="white" strokeWidth="0.5" />

					{/* Building 3 — short wide */}
					<rect x="210" y="300" width="70" height="200" fill="none" stroke="white" strokeWidth="0.8" />
					<line x1="210" y1="340" x2="280" y2="340" stroke="white" strokeWidth="0.5" />
					<line x1="210" y1="380" x2="280" y2="380" stroke="white" strokeWidth="0.5" />
					<line x1="210" y1="420" x2="280" y2="420" stroke="white" strokeWidth="0.5" />
					<line x1="210" y1="460" x2="280" y2="460" stroke="white" strokeWidth="0.5" />
					<line x1="245" y1="300" x2="245" y2="500" stroke="white" strokeWidth="0.4" />
					{/* Windows */}
					<rect x="218" y="310" width="18" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="254" y="310" width="18" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="218" y="350" width="18" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />
					<rect x="254" y="350" width="18" height="20" fill="none" stroke="white" strokeWidth="0.4" rx="1" />

					{/* Blueprint dimension lines */}
					<line x1="20" y1="500" x2="290" y2="500" stroke="white" strokeWidth="0.3" strokeDasharray="4 4" />
					<line x1="22" y1="80" x2="22" y2="500" stroke="white" strokeWidth="0.3" strokeDasharray="3 6" />

					{/* Crane hint */}
					<line x1="245" y1="300" x2="245" y2="250" stroke="white" strokeWidth="0.5" />
					<line x1="245" y1="250" x2="295" y2="250" stroke="white" strokeWidth="0.5" />
					<line x1="295" y1="250" x2="295" y2="270" stroke="white" strokeWidth="0.3" strokeDasharray="2 2" />
					<line x1="245" y1="250" x2="220" y2="250" stroke="white" strokeWidth="0.4" />
					<line x1="245" y1="255" x2="260" y2="250" stroke="white" strokeWidth="0.3" />
					<line x1="245" y1="255" x2="275" y2="250" stroke="white" strokeWidth="0.3" />
				</Box>

				{/* Menu content — centered: navigation + service categories */}
				<Box
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						px: {xs: 2, md: 3},
						pt: {xs: 10, md: 0},
						transform: `translate(${mousePos.x * 4}px, ${mousePos.y * 4}px)`,
						transition: 'transform 0.3s ease-out',
					}}
				>
					{/* Navigation links */}
					<Box
						sx={{
							display: 'flex',
							flexWrap: {xs: 'wrap', md: 'nowrap'},
							justifyContent: {xs: 'center', md: 'flex-start'},
							gap: {xs: 0.6, md: 0.8},
							mb: {xs: 3, md: 4},
							maxWidth: '700px',
							width: '100%',
							opacity: isOpen ? 1 : 0,
							transform: isOpen ? 'translateY(0)' : 'translateY(-20px)',
							transition: 'opacity 0.5s ease 0.15s, transform 0.5s ease 0.15s',
							overflow: 'visible',
							py: 1,
						}}
					>
						{[...mainLinks, ...secondaryLinks].map((link, i) => (
							<Box
								key={link.path}
								onClick={() => { setIsOpen(false); navigate(link.path); }}
								sx={{
									px: {xs: 1.5, md: 2},
									py: {xs: 0.5, md: 0.7},
									borderRadius: '30px',
									background: i < mainLinks.length
										? 'rgba(255,255,255,0.12)'
										: 'transparent',
									border: '1px solid rgba(255,255,255,0.18)',
									cursor: 'pointer',
									backdropFilter: 'blur(8px)',
									whiteSpace: 'nowrap',
									flexShrink: 0,
									transition: 'background 0.12s ease, border-color 0.12s ease, transform 0.15s ease, box-shadow 0.15s ease',
									opacity: isOpen ? 1 : 0,
									animationName: isOpen ? 'navPillIn' : 'none',
									animationDuration: '0.4s',
									animationTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
									animationDelay: `${0.2 + i * 0.04}s`,
									animationFillMode: 'both',
									'@keyframes navPillIn': {
										from: {opacity: 0, transform: 'translateY(-10px) scale(0.9)'},
										to: {opacity: 1, transform: 'translateY(0) scale(1)'},
									},
									'&:hover': {
										background: 'rgba(255,255,255,0.22)',
										borderColor: 'rgba(255,255,255,0.45)',
										transform: 'translateY(-3px) scale(1.04)',
										boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
									},
								}}
							>
								<Typography
									sx={{
										color: i < mainLinks.length ? '#fff' : 'rgba(255,255,255,0.7)',
										fontFamily: 'Titillium Web',
										fontSize: {xs: '0.75rem', md: '0.92rem'},
										fontWeight: i < mainLinks.length ? 600 : 400,
										letterSpacing: '0.04em',
										textTransform: i < mainLinks.length ? 'uppercase' : 'none',
									}}
								>
									{link.label}
								</Typography>
							</Box>
						))}
					</Box>

					{/* Service categories — with numbered index + animated underline */}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: {xs: 0.3, md: 1},
							alignItems: 'flex-start',
							maxWidth: '600px',
							width: '100%',
						}}
					>
						{serviceLinks.map((svc, i) => (
							<Box
								key={svc.index}
							onClick={() => navigate('/tjänster')}
								sx={{
									display: 'flex',
									alignItems: 'baseline',
									gap: {xs: 1.5, md: 2.5},
									cursor: 'pointer',
									py: {xs: 0.5, md: 1.2},
									width: '100%',
									position: 'relative',
									opacity: isOpen ? 1 : 0,
									transform: isOpen ? 'translateX(0)' : 'translateX(-60px)',
									transition: `opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${0.3 + i * 0.08}s, transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${0.3 + i * 0.08}s`,
									'&:hover .service-label': {
										transform: 'translateX(12px)',
									},
									'&:hover .service-line': {
										width: '100%',
									},
									'&:hover .service-number': {
										opacity: 1,
										color: 'white',
									},
								}}
							>
								<Typography
									className="service-number"
									sx={{
										color: 'rgba(255,255,255,0.3)',
										fontFamily: 'Titillium Web',
										fontSize: {xs: '0.7rem', md: '0.85rem'},
										fontWeight: 300,
										minWidth: {xs: '24px', md: '32px'},
										transition: 'all 0.4s ease',
										opacity: isOpen ? 0.6 : 0,
										transitionDelay: isOpen ? `${0.6 + i * 0.08}s` : '0s',
									}}
								>
									{String(i + 1).padStart(2, '0')}
								</Typography>
							<Box sx={{flex: 1, position: 'relative', overflow: 'hidden', pb: 0.5}}>
								<Typography
									className="service-label"
									sx={{
										color: 'white',
										fontFamily: 'Titillium Web',
										fontSize: {xs: '1.2rem', sm: '1.8rem', md: '2.4rem'},
										fontWeight: 600,
										letterSpacing: '-0.02em',
										transition: 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
										lineHeight: 1.3,
									}}
								>
									{svc.label}
								</Typography>
									{/* Animated underline */}
									<Box
										className="service-line"
										sx={{
											position: 'absolute',
											bottom: 0,
											left: 0,
											width: '0%',
											height: '1px',
											bgcolor: 'rgba(255,255,255,0.4)',
											transition: 'width 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
										}}
									/>
								</Box>
							</Box>
						))}
					</Box>
				</Box>

				{/* Bottom bar — contact CTA, language, login, LinkedIn */}
				<Box
					sx={{
						py: {xs: 2, md: 4},
						px: {xs: 2, md: 6},
						display: 'flex',
						flexDirection: {xs: 'column', md: 'row'},
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: {xs: 1.5, md: 3},
						position: 'relative',
						opacity: isOpen ? 1 : 0,
						transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
						transition: 'opacity 0.5s ease 0.7s, transform 0.5s ease 0.7s',
						'&::before': {
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							height: '2px',
							borderRadius: '1px',
							background: 'linear-gradient(90deg, transparent 0%, rgba(76,174,82,0.15) 15%, rgba(76,174,82,0.45) 40%, rgba(76,174,82,0.6) 50%, rgba(76,174,82,0.45) 60%, rgba(76,174,82,0.15) 85%, transparent 100%)',
							backgroundSize: '200% 100%',
							animation: isOpen ? 'shimmerLine 8s ease-in-out infinite' : 'none',
							boxShadow: '0 0 6px rgba(76,174,82,0.2)',
						},
						'@keyframes shimmerLine': {
							'0%': {backgroundPosition: '200% 0'},
							'100%': {backgroundPosition: '-200% 0'},
						},
					}}
				>
					{/* Left — contact CTA + info */}
					<Box sx={{display: 'flex', flexDirection: 'column', alignItems: {xs: 'center', md: 'flex-start'}, gap: 1}}>
						<Box
							onClick={() => { setIsOpen(false); navigate('/kontakt'); }}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								cursor: 'pointer',
								transition: 'all 0.25s ease',
								'&:hover .cta-text': {color: 'white'},
								'&:hover .cta-arrow': {transform: 'translateX(4px)', color: 'white'},
							}}
						>
							<Typography
								className="cta-text"
								sx={{
									color: 'rgba(255,255,255,0.85)',
									fontFamily: 'Titillium Web',
									fontSize: {xs: '1rem', md: '1.1rem'},
									fontWeight: 600,
									letterSpacing: '0.02em',
									transition: 'color 0.25s ease',
								}}
							>
								{i18next.language === 'sv'
									? 'Vi ser fram emot att höra från er'
									: 'We look forward to hearing from you'}
							</Typography>
							<Typography
								className="cta-arrow"
								sx={{
									color: 'rgba(255,255,255,0.6)',
									fontSize: '1.3rem',
									fontWeight: 300,
									transition: 'all 0.25s ease',
								}}
							>
								→
							</Typography>
						</Box>
						<Box sx={{display: 'flex', gap: {xs: 2, md: 3}, alignItems: 'center', flexWrap: 'wrap', justifyContent: {xs: 'center', md: 'flex-start'}}}>
							<Typography
								component="a"
								href="mailto:info@redmanagement.se"
								sx={{
									color: 'rgba(255,255,255,0.45)',
									fontFamily: 'Titillium Web',
									fontSize: {xs: '0.85rem', md: '0.9rem'},
									textDecoration: 'none',
									letterSpacing: '0.02em',
									'&:hover': {color: 'rgba(255,255,255,0.8)'},
								}}
							>
								info@redmanagement.se
							</Typography>
							<Typography
								sx={{
									color: 'rgba(255,255,255,0.25)',
									fontFamily: 'Titillium Web',
									fontSize: {xs: '0.85rem', md: '0.9rem'},
									letterSpacing: '0.02em',
								}}
							>
								Vasagatan 15-17, Stockholm
							</Typography>
						</Box>
					</Box>

					{/* Center — language flags + login */}
					<Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
						<Box
							onClick={() => handleChangeLanguage('sv-SE')}
							sx={{
								cursor: 'pointer',
								width: 48,
								height: 48,
								borderRadius: '50%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								transition: 'all 0.2s ease',
								'&:hover': {
									transform: 'scale(1.15)',
								},
							}}
						>
							<span className="fi fi-se" style={{width: i18next.language === 'sv' ? 36 : 28, height: i18next.language === 'sv' ? 27 : 21, borderRadius: 3, display: 'inline-block', backgroundSize: 'cover', transition: 'all 0.2s ease', boxShadow: i18next.language === 'sv' ? '0 2px 8px rgba(0,0,0,0.35)' : 'none'}} />
						</Box>
						<Box
							onClick={() => handleChangeLanguage('en-US')}
							sx={{
								cursor: 'pointer',
								width: 48,
								height: 48,
								borderRadius: '50%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								transition: 'all 0.2s ease',
								'&:hover': {
									transform: 'scale(1.15)',
								},
							}}
						>
							<span className="fi fi-gb" style={{width: i18next.language === 'en' ? 36 : 28, height: i18next.language === 'en' ? 27 : 21, borderRadius: 3, display: 'inline-block', backgroundSize: 'cover', transition: 'all 0.2s ease', boxShadow: i18next.language === 'en' ? '0 2px 8px rgba(0,0,0,0.35)' : 'none'}} />
						</Box>
						{!state.isAuthenticated && (
							<Box
								onClick={() => navigate('/login')}
								sx={{
									color: 'white',
									fontFamily: 'Titillium Web',
									fontSize: {xs: '1rem', md: '1.1rem'},
									fontWeight: 600,
									cursor: 'pointer',
									ml: 2,
									px: {xs: 3, md: 3.5},
									py: {xs: 1, md: 1.2},
									border: '2px solid rgba(255,255,255,0.6)',
									borderRadius: '28px',
									letterSpacing: '0.04em',
									transition: 'all 0.25s ease',
									'&:hover': {
										color: '#2e7d32',
										bgcolor: 'white',
										borderColor: 'white',
										transform: 'scale(1.05)',
										boxShadow: '0 4px 20px rgba(255,255,255,0.25)',
									},
								}}
							>
								{t('topbar.login')}
							</Box>
						)}
					</Box>

					{/* Right — LinkedIn */}
					<Box
						component="a"
						href="https://www.linkedin.com/company/red-management"
						target="_blank"
						rel="noopener noreferrer"
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							color: 'rgba(255,255,255,0.6)',
							textDecoration: 'none',
							transition: 'all 0.25s ease',
							'&:hover': {color: 'white'},
							'&:hover svg': {opacity: 1},
						}}
					>
						<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" style={{opacity: 0.7, transition: 'opacity 0.25s ease'}}>
							<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
						</svg>
						<Typography
							sx={{
								fontFamily: 'Titillium Web',
								fontSize: {xs: '0.95rem', md: '1.05rem'},
								letterSpacing: '0.02em',
							}}
						>
							LinkedIn
						</Typography>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default FullscreenMenu;
